import { atom } from "nanostores";

/** The touch HUD is up (spec 2p §2.1): a finger turns it on, a real key or a real mouse turns it off. */
export const $touchMode = atom(false);

/** The desktop or the touch wording of a prompt (spec 2p §2.5). */
export const hint = (desktop: string, touch: string): string => ($touchMode.get() ? touch : desktop);

const isTextField = (el: Element | null) => !!el && ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);

/** A phone's side buttons and keys nobody typed: none of them means a keyboard is in use. */
const notTyping = (key: string) => key === "Unidentified" || /^(Audio|Media|Volume)/.test(key);

/** A screen that is mainly a finger and cannot hover; a touchscreen laptop hovers with its mouse. */
export function phoneLike(mm: ((q: string) => MediaQueryList) | undefined = globalThis.matchMedia?.bind(globalThis)): boolean {
  return !!mm?.("(hover: none) and (pointer: coarse)").matches;
}

/**
 * Pointer events, not mouse events: after a tap the browser sends compatibility mousemove/mousedown/click,
 * which must not end touch mode (Review Focus 1). A screen keyboard typing into a field sends keydown too
 * (Review Focus 2), so only keys pressed outside a text field count. A phone starts in touch mode, so the
 * first prompt and toast already use the touch wording.
 */
export function bindTouchMode(target: Window = window, phone: () => boolean = phoneLike): void {
  target.addEventListener("pointerdown", (e) => { if (e.pointerType === "touch") $touchMode.set(true); }, true);
  target.addEventListener("pointermove", (e) => { if (e.pointerType === "mouse") $touchMode.set(false); }, true);
  target.addEventListener("keydown", (e) => {
    if (!isTextField(document.activeElement) && !notTyping(e.key)) $touchMode.set(false);
  }, true);
  if (phone()) $touchMode.set(true);
  $touchMode.subscribe((on) => document.body.classList.toggle("touch", on));
}
